import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Users, UserPlus, Trash, Wrench, ClockCounterClockwise, ChartLineUp, Lock,
} from "@phosphor-icons/react";

export default function Team() {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [teamInfo, setTeamInfo] = useState({ techs: [], seat_limit: 0, used_seats: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [lastCreated, setLastCreated] = useState(null); // {name, login_email, login_password}

  const pwRules = [
    { key: "len",     label: "8+ characters",         ok: form.password.length >= 8 },
    { key: "upper",   label: "One uppercase",         ok: /[A-Z]/.test(form.password) },
    { key: "number",  label: "One number",            ok: /\d/.test(form.password) },
    { key: "special", label: "One special char",      ok: /[^A-Za-z0-9]/.test(form.password) },
  ];
  const pwOk = pwRules.every((r) => r.ok);

  const load = async () => {
    try {
      const [c, t, s] = await Promise.all([
        api.get("/credits"),
        api.get("/team/techs"),
        api.get("/team/stats"),
      ]);
      setCredits(c.data);
      setTeamInfo(t.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Team management is for shop accounts only");
        navigate("/dashboard");
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addTech = async (e) => {
    e.preventDefault();
    const payload = {
      email: form.email.trim().toLowerCase(),
      name: form.name.trim(),
      password: form.password.trim(),
    };
    if (!pwOk) {
      toast.error("Password must be 8+ chars with an uppercase, a number, and a special character");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/team/techs", payload);
      toast.success(`Added ${data.name || data.email}`);
      setLastCreated(data);
      setForm({ email: "", name: "", password: "" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add tech");
    } finally { setBusy(false); }
  };

  const removeTech = async (t) => {
    if (!confirm(`Remove ${t.name || t.email}? Their diagnostic history stays but they can no longer log in.`)) return;
    try {
      await api.delete(`/team/techs/${t.user_id}`);
      toast.success("Tech removed");
      load();
    } catch { toast.error("Remove failed"); }
  };

  const seatsLeft = Math.max(0, (teamInfo.seat_limit || 0) - (teamInfo.used_seats || 0));
  const noPlan = !credits?.plan;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-xs font-mono-tech text-muted-foreground uppercase tracking-widest">// shop dashboard</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">YOUR TEAM</h1>
        <p className="text-muted-foreground mt-2">
          Add tech accounts under this shop. They share your credit pool but only see their own diagnostics.
        </p>

        {/* Value ticker */}
        {stats && (
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <StatCard
              icon={<ClockCounterClockwise weight="bold" className="w-6 h-6" />}
              value={stats.hours_saved.toLocaleString(undefined, { maximumFractionDigits: 1 })}
              suffix="hrs"
              label="Time saved by Dynamic Diag"
              sub={`Based on ${stats.minutes_per_diag} min saved per solved case`}
              testid="stat-hours-saved"
              accent
            />
            <StatCard
              icon={<ChartLineUp weight="bold" className="w-6 h-6" />}
              value={stats.total_diags}
              label="Diagnostic sessions"
              sub={`${stats.solved_diags} solved · ${stats.total_diags - stats.solved_diags} in progress`}
              testid="stat-total-diags"
            />
            <StatCard
              icon={<Users weight="bold" className="w-6 h-6" />}
              value={teamInfo.used_seats}
              suffix={` / ${teamInfo.seat_limit || 0}`}
              label="Tech seats used"
              sub={credits?.plan_name ? `${credits.plan_name} plan` : "No active plan"}
              testid="stat-seats"
            />
          </div>
        )}

        {/* Seat / plan banner */}
        {noPlan ? (
          <div className="mt-6 border border-yellow-500/40 bg-yellow-500/5 rounded-md p-5 flex items-start gap-3" data-testid="no-plan-banner">
            <Lock weight="bold" className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div className="flex-1">
              <div className="font-display text-lg text-yellow-100">Subscribe to add tech accounts</div>
              <div className="text-sm text-muted-foreground mt-1">
                Tech accounts are available on the Pro plan (2 techs) and Unlimited plan (50 techs). Pay-as-you-go is a solo tier.
              </div>
            </div>
            <Button onClick={() => navigate("/billing")} className="bg-primary hover:bg-primary/90" data-testid="upgrade-btn">
              See plans
            </Button>
          </div>
        ) : (
          <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-muted-foreground">
              <span className="font-mono-tech text-white/90">{teamInfo.used_seats}</span> of{" "}
              <span className="font-mono-tech text-white/90">{teamInfo.seat_limit}</span> seats used on the{" "}
              <span className="text-primary">{credits.plan_name}</span> plan.
              {seatsLeft > 0 ? <> {seatsLeft} seat{seatsLeft === 1 ? "" : "s"} open.</> : <> Need more? <button onClick={() => navigate("/billing")} className="text-primary hover:underline">Upgrade →</button></>}
            </div>
            <Button
              onClick={() => setShowAdd(true)}
              disabled={seatsLeft <= 0}
              className="bg-primary hover:bg-primary/90 min-h-12"
              data-testid="add-tech-btn"
            >
              <UserPlus weight="bold" className="w-4 h-4 mr-2" /> Add tech account
            </Button>
          </div>
        )}

        {/* Just-created credentials banner */}
        {lastCreated && (
          <div className="mt-4 border border-green-500/40 bg-green-500/5 rounded-md p-4 flex items-start gap-3" data-testid="new-tech-credentials">
            <UserPlus weight="bold" className="w-5 h-5 text-green-400 mt-0.5" />
            <div className="flex-1">
              <div className="font-display text-lg text-green-100">Share these with {lastCreated.name}</div>
              <div className="text-sm text-white/90 mt-1 font-mono-tech">
                Email: <span className="text-primary">{lastCreated.login_email}</span> · Password: <span className="text-primary">{lastCreated.login_password}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                They log in at <span className="font-mono-tech">{window.location.origin}/auth</span>. This message won't show again — copy it now.
              </div>
            </div>
            <button
              onClick={() => setLastCreated(null)}
              className="text-muted-foreground hover:text-white p-1"
              data-testid="dismiss-new-tech-banner"
            >✕</button>
          </div>
        )}

        {/* Add tech form */}
        {showAdd && (
          <form onSubmit={addTech} autoComplete="off" className="mt-4 border border-border rounded-md p-5 bg-card grid md:grid-cols-3 gap-3 fade-up" data-testid="add-tech-form">
            <div>
              <Label className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mike Torres" className="mt-1 min-h-12 bg-input" data-testid="new-tech-name" autoComplete="off" required />
            </div>
            <div>
              <Label className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Login email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="mike@torresauto.com" className="mt-1 min-h-12 bg-input" data-testid="new-tech-email" autoComplete="off" required />
            </div>
            <div>
              <Label className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Initial password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="8+ chars, uppercase, number, special" className="mt-1 min-h-12 bg-input font-mono-tech" data-testid="new-tech-password" autoComplete="off" name="dd-new-tech-pw" required minLength={8} />
              {form.password.length > 0 && (
                <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid="new-tech-pw-rules">
                  {pwRules.map((r) => (
                    <li
                      key={r.key}
                      data-testid={`new-tech-pw-rule-${r.key}`}
                      className={`text-[11px] font-mono-tech flex items-center gap-1.5 ${r.ok ? "text-green-400" : "text-muted-foreground"}`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${r.ok ? "bg-green-400" : "bg-muted-foreground/50"}`} />
                      {r.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="md:col-span-3 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={busy || !pwOk} className="bg-primary hover:bg-primary/90" data-testid="submit-new-tech">
                {busy ? "Adding…" : "Add tech"}
              </Button>
            </div>
          </form>
        )}

        {/* Tech list */}
        <div className="mt-8 space-y-2">
          {loading ? (
            <div className="border border-border rounded-md p-6 bg-card scan-line h-24" />
          ) : teamInfo.techs.length === 0 ? (
            <div className="text-sm text-muted-foreground border border-border rounded-md p-8 text-center" data-testid="no-techs-empty">
              No techs yet. {noPlan ? "Subscribe to a plan to invite your first one." : "Add your first tech using the button above."}
            </div>
          ) : (
            teamInfo.techs.map((t) => (
              <div key={t.user_id} className="border border-border rounded-md p-4 bg-card flex items-center justify-between gap-4" data-testid={`tech-row-${t.user_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center">
                    <Wrench weight="bold" className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-lg leading-tight truncate">{t.name}</div>
                    <div className="text-xs text-muted-foreground font-mono-tech truncate">{t.email}</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground text-right shrink-0">
                  <div>{t.diag_count} diagnostic{t.diag_count === 1 ? "" : "s"}</div>
                  <div className="mt-0.5 font-mono-tech">Added {new Date(t.created_at).toLocaleDateString()}</div>
                </div>
                <button
                  onClick={() => removeTech(t)}
                  className="text-muted-foreground hover:text-destructive p-2"
                  title="Remove tech"
                  data-testid={`remove-tech-${t.user_id}`}
                >
                  <Trash weight="bold" className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}

function StatCard({ icon, value, suffix, label, sub, testid, accent }) {
  return (
    <div className={`border rounded-md p-5 bg-card ${accent ? "border-primary/40" : "border-border"}`} data-testid={testid}>
      <div className={`flex items-center gap-2 ${accent ? "text-primary" : "text-muted-foreground"}`}>
        {icon}
        <div className="text-[10px] font-mono-tech uppercase tracking-widest">{label}</div>
      </div>
      <div className="font-display font-bold text-5xl tracking-tight mt-2">
        {value}{suffix && <span className="text-xl text-muted-foreground ml-1">{suffix}</span>}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
